
import React from 'react';
import { VorankuendigungCoordinator, VorankuendigungData, VorankuendigungParty, VorankuendigungPerson } from '../types';
import {
    contactLines,
    describeRole,
    filledCoordinators,
    filledParties,
    filledPersons,
    normalizeVorankuendigung,
} from '../services/vorankuendigungModel';

interface Props {
  data: VorankuendigungData;
  id?: string;
}

const FieldLine = ({ label, value, multiline = false }: { label: string; value: string; multiline?: boolean }) => (
  <div className="mb-4">
    <div className="flex flex-col">
      <span className="font-bold text-sm mb-1">{label}</span>
      <div className={`border-b border-gray-400 min-h-[1.5em] ${multiline ? 'whitespace-pre-wrap' : ''} text-sm px-1 pb-1 break-words`}>
        {value || " "}
      </div>
    </div>
  </div>
);

/** Zweispaltige Zeile: links Anschrift, rechts Erreichbarkeit — wie im PDF. */
const ContactRow = ({ left, right }: { left: string[]; right: string[] }) => (
  <div className="flex justify-between items-start gap-4 text-sm leading-snug">
    <div className="w-[58%] whitespace-pre-wrap break-words">{left.filter(Boolean).join('\n')}</div>
    <div className="w-[40%] flex flex-col gap-0.5 break-words">
      {right.map((line, idx) => <div key={idx}>{line}</div>)}
    </div>
  </div>
);

// Helper to split name (bold) from rest of address
const AddressBlock = ({ raw, isBox = false }: { raw: string, isBox?: boolean }) => {
    const lines = (raw || "").split('\n');
    const name = lines[0] || "";
    const rest = lines.slice(1).join('\n');

    const Content = () => (
        <div className="text-sm leading-snug">
            <div className="font-bold break-words">{name}</div>
            <div className="whitespace-pre-wrap break-words">{rest}</div>
        </div>
    );

    if (isBox) {
        return (
            <div className="border border-gray-400 w-[85mm] min-h-[40mm] p-2 flex flex-col">
                <span className="text-[10px] text-gray-500 mb-1 block">Absender (Name, Anschrift, E-Mail Adresse)</span>
                <Content />
            </div>
        );
    }
    return <Content />;
};

const HeaderSection = ({ page }: { page: string }) => (
    <div className="flex flex-col items-end mb-4">
        <h1 className="text-3xl font-bold text-black leading-none mb-2">Vorankündigung</h1>
        <div className="flex items-center gap-4 text-xs">
            <span className="text-black">gemäß BaustellV</span>
            <span className="text-gray-400 font-normal">Seite {page}</span>
        </div>
    </div>
);

const CoordinatorBlock = ({ label, list }: { label: string; list: VorankuendigungCoordinator[] }) => (
  <div className="mb-4">
    <span className="text-sm font-bold block ml-4 mb-1">{label}</span>
    <div className="border-b border-gray-400 min-h-[3em] px-1 pb-1 space-y-2">
      {list.length === 0 && <div className="text-sm">&nbsp;</div>}
      {list.map(entry => (
        <div key={entry.id}>
          <ContactRow
            left={(entry.company || '').split('\n')}
            right={[describeRole(entry.role, entry.name), ...contactLines(entry)].filter(Boolean)}
          />
        </div>
      ))}
    </div>
  </div>
);

const FormPreview: React.FC<Props> = ({ data: raw, id }) => {
  const data = normalizeVorankuendigung(raw);

  // Format address for display: Replace comma+space with newline to stack address parts
  const formattedClientAddress = data.clientAddress ? data.clientAddress.replace(/, /g, '\n') : '';

  const clientPersons: VorankuendigungPerson[] = filledPersons(data.clientPersons);
  const parties: VorankuendigungParty[] = filledParties(data.responsibleThirdParties);
  const planning = filledCoordinators(data.planningCoordinators);
  const execution = filledCoordinators(data.executionCoordinators);

  // Mindestens zwölf Zeilen, damit das Blatt weiterhin als Formular taugt.
  const employerRows = Math.max(12, (data.employersList || []).length);
  const employers = Array.from({ length: employerRows }, (_, i) => (data.employersList || [])[i] || '');

  return (
    <div id={id} data-glass="papier" className="bg-white mx-auto max-w-[210mm] shadow-lg print:shadow-none text-black font-arial">

      {/* PAGE 1 */}
      <div className="w-[210mm] min-h-[297mm] p-[20mm] relative flex flex-col print-page bg-white">

        {/* Header Title Area (Top Right) */}
        <div className="absolute top-[15mm] right-[20mm]">
            <HeaderSection page="1" />
        </div>

        {/* Spacer to push content down to where PDF starts drawing addresses (approx 32mm Y) */}
        <div className="h-[20mm]"></div>

        {/* Address Section */}
        <div className="flex items-start mb-12">
            {/* Absender (Left) */}
            <div>
                <AddressBlock raw={data.sender} isBox={true} />
            </div>

            {/* Empfänger (Right side, aligned to approx X=115mm in PDF) */}
            {/* Using margin-left to simulate the X position */}
            <div className="ml-[20mm] mt-2">
                 <AddressBlock raw={data.recipient} />
            </div>
        </div>

        {/* Content Fields Page 1 */}
        <div className="flex-grow space-y-5">
          <FieldLine
            label="1. Bezeichnung und Ort der Baustelle (Straße/Nr., PLZ/Ort):"
            value={data.siteAddress}
          />

          {/* Punkt 2: Firma links, Erreichbarkeit rechts, darunter die natürlichen Personen */}
          <div className="mb-4">
            <div className="flex flex-col">
              <span className="font-bold text-sm mb-1">2. Name und Anschrift des Bauherrn inkl. natürliche Person (Telefon, E-Mail):</span>
              <div className="border-b border-gray-400 min-h-[3.5em] text-sm px-1 pb-1">
                  <ContactRow
                    left={[data.clientName, formattedClientAddress]}
                    right={contactLines({ phone: data.clientPhone, email: data.clientEmail })}
                  />
                  {clientPersons.length > 0 && (
                    <div className="mt-2 pt-1 border-t border-dashed border-gray-300 space-y-1">
                      <div className="text-[11px] text-gray-500">Natürliche Person(en):</div>
                      {clientPersons.map(person => (
                        <div key={person.id}>
                          <ContactRow
                            left={[describeRole(person.role, person.name)]}
                            right={contactLines(person)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          </div>

          <FieldLine
            label="3. Art und Bezeichnung des Bau- und/oder Abrissvorhabens:"
            value={data.projectDescription}
            multiline
          />

          {/* Punkt 4: beliebig viele verantwortliche Dritte */}
          <div className="mb-4">
            <span className="font-bold text-sm block mb-1">4. Name und Anschrift des verantwortlichen Dritten (gemäß § 4 BauStellV):</span>
            <div className="border-b border-gray-400 min-h-[2.5em] text-sm px-1 pb-1 space-y-2">
              {parties.length === 0 && <div>&nbsp;</div>}
              {parties.map(party => (
                <div key={party.id}>
                  <ContactRow
                    left={[describeRole(party.role, party.name), party.address || '']}
                    right={contactLines(party)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Section 5: Koordinator/en */}
          <div>
            <span className="font-bold text-sm block mb-2">5. Koordinator/en mit Anschrift, Telefon und E-Mail (gemäß § 3 BauStellV):</span>
            <CoordinatorBlock label="a) für die Planung der Ausführung:" list={planning} />
            <CoordinatorBlock label="b) für die Ausführung des Bauvorhabens:" list={execution} />
          </div>
        </div>

        <div className="text-right text-xs font-bold mt-auto border-t-2 border-black pt-1">
           Bitte beachten Sie auch die Folgeseite
        </div>
      </div>

      {/* PAGE 2 */}
      <div className="w-[210mm] min-h-[297mm] p-[20mm] relative flex flex-col print-page bg-white mt-8 print:mt-0">

         {/* Header Title Area (Top Right) */}
         <div className="absolute top-[15mm] right-[20mm]">
            <HeaderSection page="2" />
        </div>

         <div className="mt-[35mm] flex-grow space-y-4">
            <div className="flex items-end border-b border-gray-400 pb-1 flex-wrap gap-y-1">
               <span className="text-sm font-bold mr-2">6. Voraussichtlicher Beginn und geplantes Ende der Arbeiten:</span>
               <span className="text-sm mx-2">von</span>
               <span className="flex-1 text-center font-bold text-sm min-w-[20mm]">{data.startDate}</span>
               <span className="text-sm mx-2">bis</span>
               <span className="flex-1 text-center font-bold text-sm min-w-[20mm]">{data.endDate}</span>
            </div>

            <div className="flex items-end border-b border-gray-400 pb-1 flex-wrap gap-y-1">
               <span className="text-sm font-bold mr-2">7. Voraussichtliche Zahl der gleichzeitig auf der Baustelle Beschäftigten:</span>
               <span className="flex-1 text-left font-medium pl-2 text-sm break-words">{data.maxWorkers}</span>
            </div>

            <div className="flex items-end border-b border-gray-400 pb-1 flex-wrap gap-y-1">
               <span className="text-sm font-bold mr-2">8. Voraussichtliche Zahl der Arbeitgebenden:</span>
               <span className="flex-1 text-left font-medium pl-2 text-sm break-words">{data.numEmployers}</span>
            </div>

            <div className="flex items-end border-b border-gray-400 pb-1 flex-wrap gap-y-1">
               <span className="text-sm font-bold mr-2">9. Voraussichtliche Zahl der Unternehmer/innen ohne Beschäftigte:</span>
               <span className="flex-1 text-left font-medium pl-2 text-sm break-words">{data.numContractors}</span>
            </div>

            <div className="mb-2 pt-4">
               <span className="text-sm font-bold block mb-4">10. Bereits ausgewählte Arbeitgebende:</span>
               <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-sm">
                  {employers.map((emp, idx) => (
                    <div key={idx} className="flex items-end border-b border-gray-400 pb-1">
                      <span className="w-6 text-gray-500 text-xs mb-[1px]">{idx + 1}.</span>
                      <span className="flex-1 min-h-[1.25rem] font-medium break-words">{emp}</span>
                    </div>
                  ))}
               </div>
            </div>

            <div className="mb-4 pt-4">
              <div className="text-sm font-bold mb-1">11. Auf der Baustelle werden besonders gefährliche Arbeiten i. S. v. § 2 (3) BauStellV durchgeführt:</div>
              <div className="flex items-start mt-2">
                 <div className="flex items-center mr-6">
                    <div className="w-4 h-4 border border-black mr-2 flex items-center justify-center">
                       {!data.hasDangerousWork && <div className="w-3 h-3 bg-black"></div>}
                    </div>
                    <span className="text-sm">nein</span>
                 </div>
                 <div className="flex items-center mr-4">
                    <div className="w-4 h-4 border border-black mr-2 flex items-center justify-center">
                       {data.hasDangerousWork && <div className="w-3 h-3 bg-black"></div>}
                    </div>
                    <span className="text-sm whitespace-nowrap">ja - wenn ja, welche?</span>
                 </div>
              </div>
              <div className="border-b border-gray-400 min-h-[1.5em] mt-1 text-sm whitespace-pre-wrap pb-1 break-words">
                 {data.hasDangerousWork ? data.dangerousWorkDescription : ""}
              </div>
              <div className="text-[11px] text-gray-500 font-medium mt-1 leading-tight">
                 (Arbeiten mit Versinken, Verschüttetwerden, Absturz {'>'} 7m, Explosivstoffe, Strahlung, etc.)
              </div>
            </div>

            {data.hasSiteManager && (
              <FieldLine
                label="12. Verantwortliche Bauleitung nach LBO / HBauO (Name, Telefon):"
                value={data.siteManager}
              />
            )}
         </div>

         {/* Footer */}
         <div className="mt-auto pt-8 pb-4">
            <div className="flex space-x-8 mb-6">
               <div className="w-1/3">
                  <div className="border-b border-black h-8 flex items-end text-sm pb-1 font-medium">{data.locationDate}</div>
                  <div className="text-[11px] mt-1">Ort, Datum</div>
               </div>
               <div className="w-1/3">
                  <div className="border-b border-black h-8 flex items-end text-sm pb-1 font-medium">{data.signerName}</div>
                  <div className="text-[11px] mt-1">Name</div>
               </div>
               <div className="w-1/3">
                  <div className="border-b border-black h-8"></div>
                  <div className="text-[11px] mt-1">Unterschrift des Bauherrn / Verantwortlichen</div>
               </div>
            </div>

            <h2 className="text-sm font-bold leading-tight">
               Die Vorankündigung ist sichtbar auf der Baustelle auszuhängen.
            </h2>
            <div className="border-b border-black mt-2 w-full"></div>
         </div>
      </div>
    </div>
  );
};

export default FormPreview;
